import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Grid, Divider, FormControlLabel, Switch, Card, CardContent,
  Alert, CircularProgress, IconButton, InputAdornment, Radio, RadioGroup, FormLabel
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff, Save, Cancel } from '@mui/icons-material';
import { styled } from '@mui/system';

import useAdd from '../../api/useAdd';
import useFetchObjects from '../../api/useFetchObjects';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

// Styled Components
const RTLTextField = styled(TextField)(({ isrtl }) => ({
  ...(isrtl === 'true' && {
    '& .MuiInputBase-input': { textAlign: 'right', direction: 'rtl', padding: '10px 14px 16px' },
    '& .MuiInputLabel-root': { right: 25, left: 'auto', transformOrigin: 'right', marginRight: '5px' },
    '& .MuiOutlinedInput-root': { paddingLeft: 14, paddingRight: 0 },
    '& .MuiInputAdornment-root': { marginRight: 8, marginLeft: 0 },
    '& .MuiOutlinedInput-notchedOutline, & .MuiFormHelperText-root': { textAlign: 'right' }
  })
}));

const RTLFormControl = styled(FormControl)(({ isrtl }) => ({
  ...(isrtl === 'true' && {
    '& .MuiInputLabel-root': { right: 25, left: 'auto', transformOrigin: 'right', marginRight: '5px' },
    '& .MuiInputBase-input, & .MuiSelect-select': { textAlign: 'right', direction: 'rtl' },
    '& .MuiSelect-select': { paddingRight: '32px', paddingLeft: '32px' },
    '& .MuiSelect-icon': { right: 'auto', left: 7, pointerEvents: 'none' },
    '& .MuiFormHelperText-root': { textAlign: 'right' }
  })
}));

const UserCreate = () => {
  const { t, i18n } = useTranslation(['users', 'common', 'validation']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';

  // State
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '', password: '', email: '', position: '', role_id: '', ministry_id: '', deputy_ministry_id: '',
    directorate_parent_type: 'ministry', directorate_id: '', department_parent_type: 'directorate', 
    department_id: '', is_provincial: false, province_id: '', is_active: true
  });
  const [errors, setErrors] = useState({});
  const [filteredDeputyMinistries, setFilteredDeputyMinistries] = useState([]);
  const [filteredDirectorates, setFilteredDirectorates] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);

  // Data fetching - Set high limits to get all records for dropdowns
  const { data: rolesData } = useFetchObjects('roles', { limit: 1000, page: 1 }, token);
  const { data: ministriesData } = useFetchObjects('ministries', { limit: 1000, page: 1 }, token);
  const { data: deputyMinistriesData } = useFetchObjects('deputy-ministries', { limit: 1000, page: 1 }, token);
  const { data: directoratesData } = useFetchObjects('directorates', { limit: 1000, page: 1 }, token);
  const { data: departmentsData } = useFetchObjects('departments', { limit: 1000, page: 1 }, token);
  const { data: provincesData } = useFetchObjects('provinces', { limit: 1000, page: 1 }, token);
  const { handleAdd, loading: isCreating, success: createSuccess } = useAdd('users', token, '/users', t('create_success'), t('create_error'));

  // Normalize potential API payload shapes: some endpoints return array directly, others wrap in { data: [] }
  const unwrap = (d) => (Array.isArray(d) ? d : (d && Array.isArray(d.data) ? d.data : []));

  const roles = unwrap(rolesData);
  const ministries = unwrap(ministriesData);
  const deputyMinistries = unwrap(deputyMinistriesData);
  const directorates = unwrap(directoratesData);
  const departments = unwrap(departmentsData);
  const provinces = unwrap(provincesData);

  // Filter effects
  useEffect(() => {
    if (formData.ministry_id) {
      const filtered = deputyMinistries?.filter(dept => dept.ministry_id === formData.ministry_id) || [];
      setFilteredDeputyMinistries(filtered);
      if (formData.deputy_ministry_id && !filtered.some(dept => dept.id === formData.deputy_ministry_id)) {
        setFormData(prev => ({ ...prev, deputy_ministry_id: '', directorate_id: '', department_id: '' }));
      }
    } else {
      setFilteredDeputyMinistries([]);
      setFormData(prev => ({ ...prev, deputy_ministry_id: '', directorate_id: '', department_id: '' }));
    }
  }, [formData.ministry_id, deputyMinistries]);

  useEffect(() => {
    let filtered = [];
    if (formData.directorate_parent_type === 'ministry' && formData.ministry_id) {
      filtered = directorates?.filter(dir => dir.ministry_id === formData.ministry_id && !dir.deputy_ministry_id) || [];
    } else if (formData.directorate_parent_type === 'deputy' && formData.deputy_ministry_id) {
      filtered = directorates?.filter(dir => dir.deputy_ministry_id === formData.deputy_ministry_id) || [];
    }
    setFilteredDirectorates(filtered);
    setFormData(prev => ({ ...prev, directorate_id: '', department_id: '' }));
  }, [formData.directorate_parent_type, formData.ministry_id, formData.deputy_ministry_id, directorates]);

  useEffect(() => {
    let filtered = [];
    if (formData.department_parent_type === 'directorate' && formData.directorate_id) {
      filtered = departments?.filter(dept => dept.directorate_id === formData.directorate_id) || [];
    } else if (formData.department_parent_type === 'deputy' && formData.deputy_ministry_id) {
      filtered = departments?.filter(dept => dept.deputy_ministry_id === formData.deputy_ministry_id && !dept.directorate_id) || [];
    }
    setFilteredDepartments(filtered);
    setFormData(prev => ({ ...prev, department_id: '' }));
  }, [formData.department_parent_type, formData.directorate_id, formData.deputy_ministry_id, departments]);

  useEffect(() => {
    if (createSuccess) navigate('/users');
  }, [createSuccess, navigate]);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev, 
      [name]: checked,
      ...(name === 'is_provincial' && !checked && { province_id: '' })
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = t('validation:required');
    if (!formData.password.trim()) newErrors.password = t('validation:required');
    else if (formData.password.length < 6) newErrors.password = t('validation:password_length');
    if (!formData.email.trim()) newErrors.email = t('validation:required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('validation:invalid_email');
    if (!formData.position.trim()) newErrors.position = t('validation:required');
    if (!formData.role_id) newErrors.role_id = t('validation:required');
    if (!formData.ministry_id) newErrors.ministry_id = t('validation:required');
    if (formData.is_provincial && !formData.province_id) newErrors.province_id = t('validation:required');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    const { directorate_parent_type, department_parent_type, ...submissionData } = formData;
    handleAdd(submissionData);
  };

  // Loading state
  const isLoading = !rolesData || !ministriesData || !deputyMinistriesData || !directoratesData || !departmentsData || !provincesData;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        mb: 3, flexDirection: isRtl ? 'row-reverse' : 'row', width: '100%'
      }}>
        <Button
          component={RouterLink}
          to="/users"
          startIcon={!isRtl && <ArrowBack />}
          endIcon={isRtl && <ArrowBack />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('common:back')}
        </Button>
        <Typography variant="h4" component="h1" sx={{ textAlign: isRtl ? 'right' : 'left', fontWeight: 'bold' }}>
          {t('create_title')}
        </Typography>
      </Box>
      
      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            {/* Account Info Section */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, textAlign: isRtl ? 'right' : 'left', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              {t('account_info')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth required
                  name="username" label={t('username')}
                  value={formData.username} onChange={handleInputChange}
                  error={!!errors.username} helperText={errors.username}
                  isrtl={isRtl ? 'true' : 'false'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth required
                  name="email" label={t('email')} type="email"
                  value={formData.email} onChange={handleInputChange}
                  error={!!errors.email} helperText={errors.email}
                  isrtl={isRtl ? 'true' : 'false'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth required
                  name="password" label={t('password')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password} onChange={handleInputChange}
                  error={!!errors.password} helperText={errors.password}
                  isrtl={isRtl ? 'true' : 'false'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth required
                  name="position" label={t('position')}
                  value={formData.position} onChange={handleInputChange}
                  error={!!errors.position} helperText={errors.position}
                  isrtl={isRtl ? 'true' : 'false'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLFormControl fullWidth required error={!!errors.role_id} isrtl={isRtl ? 'true' : 'false'}>
                  <InputLabel>{t('role')}</InputLabel>
                  <Select
                    size="small"
                    name="role_id" label={t('role')}
                    value={formData.role_id} onChange={handleInputChange}
                  >
                    {roles.map(role => (
                      <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                    ))}
                  </Select>
                </RTLFormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={formData.is_active} onChange={handleSwitchChange} name="is_active" color="success" />}
                  label={t('active_status')}
                  sx={{ flexDirection: isRtl ? 'row-reverse' : 'row' }}
                />
              </Grid>
            </Grid>

            {/* Organization Info Section */}
            <Typography variant="h6" sx={{ mt: 4, mb: 3, fontWeight: 600, textAlign: isRtl ? 'right' : 'left', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              {t('organization_info')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <RTLFormControl fullWidth required error={!!errors.ministry_id} isrtl={isRtl ? 'true' : 'false'}>
                  <InputLabel>{t('ministry')}</InputLabel>
                  <Select
                    size="small"
                    name="ministry_id" label={t('ministry')}
                    value={formData.ministry_id} onChange={handleInputChange}
                  >
                    {ministries.map(ministry => (
                      <MenuItem key={ministry.id} value={ministry.id}>{ministry.name}</MenuItem>
                    ))}
                  </Select>
                </RTLFormControl>
              </Grid>
              
              {formData.ministry_id && (
                <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth isrtl={isRtl ? 'true' : 'false'}>
                    <InputLabel>{t('deputy_ministry')}</InputLabel>
                    <Select
                      size="small"
                      name="deputy_ministry_id" label={t('deputy_ministry')}
                      value={formData.deputy_ministry_id} onChange={handleInputChange}
                    >
                      {filteredDeputyMinistries.map(dm => (
                        <MenuItem key={dm.id} value={dm.id}>{dm.name}</MenuItem>
                      ))}
                    </Select>
                  </RTLFormControl>
                </Grid>
              )}
              
              {formData.ministry_id && (
                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ textAlign: isRtl ? 'right' : 'left', width: '100%', mb: 1 }}>
                      {t('directorate')} {t('common:parent_type')}
                    </FormLabel>
                    <RadioGroup
                      row
                      name="directorate_parent_type"
                      value={formData.directorate_parent_type}
                      onChange={handleInputChange}
                      sx={{ flexDirection: 'row', justifyContent: isRtl ? 'flex-start' : 'flex-start', width: '100%' }}
                    >
                      <FormControlLabel
                        value="ministry"
                        size="small"
                        control={<Radio />}
                        label={t('ministry')}
                        disabled={!formData.ministry_id}
                      />
                      <FormControlLabel
                        value="deputy"
                        size="small"
                        control={<Radio />}
                        label={t('deputy_ministry')}
                        disabled={!formData.deputy_ministry_id}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
              
              {((formData.directorate_parent_type === 'ministry' && formData.ministry_id) || 
                (formData.directorate_parent_type === 'deputy' && formData.deputy_ministry_id)) && (
                <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth isrtl={isRtl ? 'true' : 'false'}>
                    <InputLabel>{t('directorate')}</InputLabel>
                    <Select
                      size="small"
                      name="directorate_id" label={t('directorate')}
                      value={formData.directorate_id} onChange={handleInputChange}
                    >
                      {filteredDirectorates.map(dir => (
                        <MenuItem key={dir.id} value={dir.id}>{dir.name}</MenuItem>
                      ))}
                    </Select>
                  </RTLFormControl>
                </Grid>
              )}
              
              {formData.deputy_ministry_id && (
                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ textAlign: isRtl ? 'right' : 'left', width: '100%', mb: 1 }}>
                      {t('department')} {t('common:parent_type')}
                    </FormLabel>
                    <RadioGroup
                      row
                      name="department_parent_type"
                      value={formData.department_parent_type}
                      onChange={handleInputChange}
                      sx={{ flexDirection: 'row', justifyContent: isRtl ? 'flex-start' : 'flex-start', width: '100%' }}
                    >
                      <FormControlLabel
                        value="directorate"
                        size="small"
                        control={<Radio />}
                        label={t('directorate')}
                        disabled={!formData.directorate_id}
                      />
                      <FormControlLabel
                        value="deputy"
                        size="small"
                        control={<Radio />}
                        label={t('deputy_ministry')}
                        disabled={!formData.deputy_ministry_id}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
              
              {((formData.department_parent_type === 'directorate' && formData.directorate_id) || 
                (formData.department_parent_type === 'deputy' && formData.deputy_ministry_id)) && (
                <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth isrtl={isRtl ? 'true' : 'false'}>
                    <InputLabel>{t('department')}</InputLabel>
                    <Select
                      size="small"
                      name="department_id" label={t('department')}
                      value={formData.department_id} onChange={handleInputChange}
                    >
                      {filteredDepartments.map(dept => (
                        <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                      ))}
                    </Select>
                  </RTLFormControl>
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={formData.is_provincial} onChange={handleSwitchChange} name="is_provincial" color="primary" />}
                  label={t('is_provincial')}
                  sx={{ flexDirection: isRtl ? 'row-reverse' : 'row' }}
                />
              </Grid>
              
              {formData.is_provincial && (
                <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth required error={!!errors.province_id} isrtl={isRtl ? 'true' : 'false'}>
                    <InputLabel>{t('province')}</InputLabel>
                    <Select
                      size="small"
                      name="province_id" label={t('province')}
                      value={formData.province_id} onChange={handleInputChange}
                    >
                      {provinces.map(province => (
                        <MenuItem key={province.id} value={province.id}>{province.name}</MenuItem>
                      ))}
                    </Select>
                  </RTLFormControl>
                </Grid>
              )}
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/users"
                startIcon={!isRtl && <Cancel />}
                endIcon={isRtl && <Cancel />}
              >
                {t('common:cancel')}
              </Button>
              <Button 
                variant="contained" 
                type="submit"
                disabled={isCreating}
                startIcon={isCreating ? <CircularProgress size={20} /> : <Save />}
              >
                {t('common:save')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserCreate;